// Athlete search shared by the Lists page: the smart-bar token parser, the filter
// vocabulary and the query-string builder. Lifted verbatim from AdminReportBuilderPage.js
// (since deleted when it was merged into the Lists page) so the parsing is not rewritten.

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];


// Short football position codes recognized by the smart bar + used as the
// Position dropdown values for football reports. Stored filter value is the
// CODE; it's expanded to an ILIKE substring only when building the query
// (backend AthleteProfile.position_primary stores the long name).
export const POS_CODES = ['QB', 'RB', 'WR', 'TE', 'OL', 'DL', 'EDGE', 'LB', 'CB', 'S', 'K', 'P', 'ATH'];
export const FB_CODE_TO_API = {
  QB: 'Quarterback', RB: 'Running Back', WR: 'Wide Receiver', TE: 'Tight End',
  OL: 'Offensive', DL: 'Defensive', EDGE: 'Defensive End', LB: 'Linebacker',
  CB: 'Cornerback', S: 'Safety', K: 'Kicker', P: 'Punter', ATH: 'Athlete',
};

// Numeric filter fields — internal key → API param + chip metadata.
// op '<=' maps to a *_max param, '>=' to a *_min param.
export const NUM_FIELDS = {
  htMin: { chip: 'HT', op: '>=', param: 'height_min', menu: 'Height (min)', step: '1', placeholder: '74' },
  wtMin: { chip: 'WT', op: '>=', param: 'weight_min', menu: 'Weight (min)', step: '1', placeholder: '200' },
  dashMax: { chip: '40YD', op: '<=', param: 'dash_40_max', menu: '40yd Dash (max)', step: '0.01', placeholder: '4.6' },
  vertMin: { chip: 'VERT', op: '>=', param: 'vertical_jump_min', menu: 'Vertical (min)', step: '1', placeholder: '32' },
  gpaMin: { chip: 'GPA', op: '>=', param: 'gpa_weighted_min', menu: 'GPA (min)', step: '0.1', placeholder: '3.5' },
  satMin: { chip: 'SAT', op: '>=', param: 'sat_min', menu: 'SAT (min)', step: '10', placeholder: '1200' },
  actMin: { chip: 'ACT', op: '>=', param: 'act_min', menu: 'ACT (min)', step: '1', placeholder: '28' },
};

export const EMPTY_FILTERS = { q: '', pos: '', grad: '', state: '', school: '', camp: '', htMin: '', wtMin: '', dashMax: '', vertMin: '', gpaMin: '', satMin: '', actMin: '' };

const campNorm = (name) => (name || '').toLowerCase().replace(/[\s_]+/g, ' ').trim();

export function parseSmart(text, campList = [], positions = []) {
  const f = { ...EMPTY_FILTERS };
  const qWords = [];
  // Non-football sports have no short codes, so the sport's own position names (from the catalog) are
  // matched as whole words, longest first so "Point Guard" wins over a shorter name inside it. Matched
  // names are lifted out of the text before it is split into tokens.
  let rest = text || '';
  const names = [...positions].filter(Boolean).sort((x, y) => y.length - x.length);
  for (const name of names) {
    const esc = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(^|\\s)${esc}(?=\\s|$)`, 'i');
    if (!f.pos && re.test(rest)) { f.pos = name; rest = rest.replace(re, ' '); }
  }
  for (const raw of rest.trim().split(/\s+/)) {
    if (!raw) continue;
    const tok = raw.toUpperCase();
    // camp:<substring> → the camp whose name is exactly the token, else the first camp whose name
    // contains it (case-insensitive). `_` stands for a space, so a whole name fits in one token.
    const campTok = raw.match(/^camp:(.+)$/i);
    if (campTok) {
      const needle = campNorm(campTok[1]);
      const hit = campList.find((c) => campNorm(c.name) === needle)
        || campList.find((c) => campNorm(c.name).includes(needle));
      if (hit) f.camp = String(hit.id);
      continue; // unmatched camp: tokens are dropped rather than falling back to name search
    }
    const cmp = raw.match(/^(40|dash|vert|vertical|gpa|ht|height|wt|weight|sat|act)(<=|>=|<|>|=)(.+)$/i);
    if (cmp) {
      const stat = cmp[1].toLowerCase();
      const op = cmp[2];
      let val = cmp[3];
      const h = val.match(/^(\d)'(\d{1,2})"?$/); // 6'2 → inches
      if (h) val = String(+h[1] * 12 + +h[2]);
      if (isNaN(parseFloat(val))) { qWords.push(raw); continue; }
      const lt = op === '<' || op === '<=';
      if ((stat === '40' || stat === 'dash') && lt) f.dashMax = val;
      else if ((stat === 'vert' || stat === 'vertical') && !lt) f.vertMin = val;
      else if (stat === 'gpa' && !lt) f.gpaMin = val;
      else if ((stat === 'ht' || stat === 'height') && !lt) f.htMin = val;
      else if ((stat === 'wt' || stat === 'weight') && !lt) f.wtMin = val;
      else if (stat === 'sat' && !lt) f.satMin = val;
      else if (stat === 'act' && !lt) f.actMin = val;
      continue;
    }
    if (POS_CODES.includes(tok)) { f.pos = tok; continue; }
    if (/^20\d{2}$/.test(tok)) { f.grad = tok; continue; }
    if (tok.length === 2 && US_STATES.includes(tok)) { f.state = tok; continue; }
    qWords.push(raw);
  }
  f.q = qWords.join(' ');
  return f;
}

/**
 * Query string for GET /athletes/search from a filter set. Lifted from the List Builder's
 * debounced search effect so the Lists page and any future caller build it the same way.
 */
export function buildSearchParams(f, sport, isFootball, limit = 50) {
  const params = new URLSearchParams();
  if (f.q) params.append('search', f.q);
  if (f.pos) params.append('position', isFootball ? (FB_CODE_TO_API[f.pos] || f.pos) : f.pos);
  if (f.grad) params.append('graduation_year', f.grad);
  if (f.state) params.append('state', f.state);
  if (f.school) params.append('high_school', f.school);
  if (f.camp) params.append('camp_id', f.camp); // restricts to athletes who attended that camp
  for (const [key, def] of Object.entries(NUM_FIELDS)) {
    if (f[key]) params.append(def.param, f[key]);
  }
  params.append('sport', sport);
  params.append('limit', String(limit));
  return params;
}

// Typed form of each numeric filter, so a chip change does not drop a limit someone typed.
const NUM_TOKENS = {
  htMin: 'ht>=', wtMin: 'wt>=', dashMax: '40<=', vertMin: 'vert>=', gpaMin: 'gpa>=', satMin: 'sat>=', actMin: 'act>=',
};

/**
 * The smart-bar text for a filter set: the inverse of parseSmart for the name search, position,
 * class year, state, camp and the numeric limits. Chips on the Lists page write through this so
 * the chips and the typed text never disagree. A camp is written as `camp:<Whole_Name>`; a camp id
 * not in campList is dropped.
 */
export function filtersToText(f, campList = []) {
  const parts = [];
  if (f.q) parts.push(f.q.trim());
  if (f.pos) parts.push(f.pos);
  if (f.grad) parts.push(String(f.grad));
  if (f.state) parts.push(f.state);
  if (f.camp) {
    const camp = campList.find((c) => String(c.id) === String(f.camp));
    if (camp && camp.name) parts.push(`camp:${camp.name.trim().replace(/\s+/g, '_')}`);
  }
  for (const [key, tok] of Object.entries(NUM_TOKENS)) {
    if (f[key]) parts.push(`${tok}${f[key]}`);
  }
  return parts.filter(Boolean).join(' ');
}
